/**
 * @file: ChatSearchView.tsx
 *
 * Chat search content — navigation-agnostic. Searches threads + contacts
 * (logic in `useChatSearch`) and emits the picked row via `onPick` (a
 * chat-native ChatSearchPick); the host opens the corresponding conversation.
 * `onCancel` dismisses. Embeddable (desktop overlay + mobile screen).
 */

import { Typography } from '@euler/app/components/typography';
import { ChatSearchPick } from '@euler/app/flows/chat/lib/types';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatContact, ChatThreadItem } from '@euler/model/chat';
import { Search, X } from 'lucide-react-native';
import { FC, memo, useCallback } from 'react';
import {
  FlatList,
  Keyboard,
  ListRenderItem,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SearchResult, useChatSearch } from '../functions/useChatSearch';
import { RecentChats } from './RecentChats';
import { SearchResultRow, SearchResultSeparator } from './SearchResultRow';

export const ChatSearchView: FC<{
  onPick: (pick: ChatSearchPick) => void;
  onCancel: () => void;
  /** Mounted inside a panel — the panel owns the title/close, so drop the
   * inline 取消 and the screen safe-area. */
  embedded?: boolean;
}> = memo(({ onPick, onCancel, embedded }) => {
  const { rpx } = useResponsiveContext();
  const insets = useSafeAreaInsets();
  const { query, setQuery, results } = useChatSearch();

  const onThreadPress = useCallback(
    (thread: ChatThreadItem, displayName: string) =>
      onPick({ kind: 'thread', thread, displayName }),
    [onPick],
  );
  const onContactPress = useCallback(
    (contact: ChatContact) => onPick({ kind: 'contact', contact }),
    [onPick],
  );

  const renderItem: ListRenderItem<SearchResult> = useCallback(
    ({ item }) => (
      <SearchResultRow
        item={item}
        onThreadPress={onThreadPress}
        onContactPress={onContactPress}
      />
    ),
    [onThreadPress, onContactPress],
  );

  const keyExtractor = useCallback(
    (item: SearchResult) =>
      item.kind === 'thread' ? `t:${item.data.id}` : `c:${item.data.memberId}`,
    [],
  );

  return (
    <View
      style={{
        flex: 1,
        paddingTop: embedded ? 0 : insets.top,
        paddingBottom: embedded ? 0 : insets.bottom,
        backgroundColor: '#f5f5f5',
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          padding: rpx(12),
          backgroundColor: '#fff',
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: '#e0e0e0',
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            flex: 1,
            backgroundColor: '#f3f4f6',
            borderRadius: rpx(8),
            paddingHorizontal: rpx(10),
            height: rpx(40),
          }}
        >
          <Search size={rpx(16)} color="#9ca3af" />
          <TextInput
            value={query}
            onChangeText={setQuery}
            placeholder="搜索联系人、群聊或消息"
            style={{
              flex: 1,
              marginLeft: rpx(6),
              fontSize: rpx(14),
              fontFamily: FontFamily.NotoSans.Regular,
              color: '#111',
              padding: 0,
            }}
            placeholderTextColor="#9ca3af"
          />
          {query ? (
            <TouchableOpacity onPress={() => setQuery('')}>
              <X size={rpx(16)} color="#9ca3af" />
            </TouchableOpacity>
          ) : null}
        </View>
        {!embedded ? (
          <TouchableOpacity onPress={onCancel} style={{ marginLeft: rpx(12) }}>
            <Typography.Text size={rpx(14)} color="#3b82f6">
              取消
            </Typography.Text>
          </TouchableOpacity>
        ) : null}
      </View>

      {/* Tap anywhere off the input (blank space, list background) dismisses
          the keyboard; row taps still register via their own touchables and
          `keyboardShouldPersistTaps`. */}
      <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
        <View style={{ flex: 1 }}>
          {query ? (
            results.length === 0 ? (
              <View
                style={{
                  flex: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography.Text size={rpx(14)} color="#9ca3af">
                  没有找到匹配的结果
                </Typography.Text>
              </View>
            ) : (
              <FlatList
                data={results}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                ItemSeparatorComponent={SearchResultSeparator}
                keyboardShouldPersistTaps="handled"
                keyboardDismissMode="on-drag"
              />
            )
          ) : (
            <RecentChats onPick={onPick} />
          )}
        </View>
      </TouchableWithoutFeedback>
    </View>
  );
});
